import { NetworkStatus, useQuery } from '@apollo/client';
import { ErrorBoundary, SearchField } from '@bechtel/client-common';
import { GET_MATERIALS } from '@bechtel/client-gql';
import {
  belowBreakpoint,
  Box,
  Button,
  ButtonSize,
  FormControl,
  Heading,
  Icon,
  IconSize,
  Select,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetHeader,
  SideSheetSize,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  Theme,
  theme,
  useMediaQuery,
} from '@bechtel/gds';
import { Material, WorkPackage } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, get, includes, join, map, reduce, sortBy, toLower, uniqBy } from 'lodash';
import { ChangeEvent, FC, Suspense, useContext, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { useWorkPackage } from '../context';
import { TableStyled, TdStyled, ThStyled } from './styled';
interface SortField {
  title: string;
  name: keyof Material;
}

const sortFields: SortField[] = [
  { title: t`Tag or Stock Code`, name: 'tagOrStockCode' },
  { title: t`ISO`, name: 'iso' },
  { title: t`Balance`, name: 'balance' },
  { title: t`On Hand`, name: 'onHand' },
];

const [initialSortField] = sortFields;

const SelectedSortItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return <Text size="400">{i18n._(item.title)}</Text>;
};

const SortItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text family="graphikCompact" size="400">
      {i18n._(item.title)}
    </Text>
  );
};

interface MaterialProps {
  wpId: string;
}

const MaterialsTable: FC<MaterialProps> = ({ wpId }) => {
  const { loading, error, data, refetch, networkStatus } = useQuery<
    {
      workPackagesByIds: WorkPackage[];
    },
    {
      ids: string[];
    }
  >(GET_MATERIALS, {
    variables: {
      ids: [wpId],
    },
    notifyOnNetworkStatusChange: true,
  });

  const [search, setSearch] = useState('');
  const [sortValue, setSortValue] = useState(initialSortField.name);
  const [filterValue, setFilterValue] = useState(t`All`);
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const defaultMaterials = get(data, 'workPackagesByIds[0].materials', []);

  const filteredMaterials = filter(defaultMaterials, (m: Material) => {
    const filterCriteria = filterValue == t`All` ? '' : toLower(filterValue);
    const mwrValue = toLower(map(m.mwrs, 'mwrNumber').join(','));
    return includes(mwrValue, filterCriteria);
  });

  const searchedMaterials = filter(filteredMaterials, (m: Material) => {
    const searchValue = toLower(search);
    const description = toLower(m.description || '');
    const tagValue = toLower(m.tagOrStockCode);
    const isoValue = toLower(m.iso || '');
    const mwrValue = toLower(map(m.mwrs, 'mwrNumber').join(','));
    const mwrStatus = toLower(map(m.mwrs, 'mwrStatus').join(','));
    return (
      includes(description, searchValue) ||
      includes(tagValue, searchValue) ||
      includes(isoValue, searchValue) ||
      includes(mwrValue, searchValue) ||
      includes(mwrStatus, searchValue)
    );
  });

  const sortedMaterials = sortBy(searchedMaterials, [sortValue]);

  const mwrNumberList = uniqBy(
    reduce(
      defaultMaterials,
      (items: any, item: Material) => {
        item.mwrs.map((item) => {
          items.push({ title: item.mwrNumber, name: item.mwrNumber });
        });
        return items;
      },
      [{ title: t`All`, name: 'All' }]
    ),
    'name'
  );
  const [initialFilterField] = mwrNumberList;

  return (
    <Box p={{ sm: '500' }} flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} data-testid="material-table">
      <Box pt={{ sm: '400' }} pb={{ sm: '600' }}>
        <Box css={{ flex: 1 }}>
          <SearchField
            type="search"
            iconSize={IconSize.MEDIUM}
            placeholder={t`Search`}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            style={{ fontFamily: `${theme.fontFamilies.graphik}` }}
          />
        </Box>
        <Box mx={{ sm: '600' }} css={{ flex: 1 }}>
          <FormControl
            label={t`Filter By`}
            css={`
              flex-grow: 1;
              font-family: ${({ theme }) => theme.fontFamilies.graphik};
            `}
          >
            <Select
              size={TextFieldSize.MEDIUM}
              appearance={TextFieldAppearance.NORMAL}
              items={mwrNumberList}
              itemComponent={SortItem}
              selectedComponent={SelectedSortItem}
              initialSelectedItem={initialFilterField}
              onSelectedItemChange={(changes: any) => {
                setFilterValue(changes.selectedItem.name);
              }}
            />
          </FormControl>
        </Box>
        <Box css={{ flex: 1 }}>
          <FormControl
            label={t`Sort By`}
            css={`
              flex-grow: 1;
              font-family: ${({ theme }) => theme.fontFamilies.graphik};
            `}
          >
            <Select
              size={TextFieldSize.MEDIUM}
              appearance={TextFieldAppearance.NORMAL}
              items={sortFields}
              itemComponent={SortItem}
              selectedComponent={SelectedSortItem}
              initialSelectedItem={initialSortField}
              onSelectedItemChange={(changes: any) => {
                setSortValue(changes.selectedItem.name);
              }}
            />
          </FormControl>
        </Box>
      </Box>

      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box css="height: 100%;" data-testid="loading">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        </Box>
      )}

      {!loading && error && (
        <Box css="height: 100%;" data-testid="retry">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
              <Text>
                <Trans>There was a problem loading Materials</Trans>
              </Text>
              <Text>
                <Trans>Please click the button below to try again.</Trans>
              </Text>
            </Box>
            <Box>
              <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {!loading && !error && (
        <TableStyled data-testid="data-loaded">
          <thead>
            <tr data-testid="material-heading">
              <ThStyled style={isDesktop ? { width: '37%' } : { width: '34%' }}>
                <Trans>Tag or Stock Code</Trans>
              </ThStyled>
              <ThStyled style={isDesktop ? { width: '13%' } : { width: '19%' }}>
                <Trans>ISO</Trans>
              </ThStyled>
              <ThStyled style={isDesktop ? { width: '16%' } : { width: '15%' }}>
                <Trans>Design/Issue/Balance</Trans>
              </ThStyled>
              <ThStyled style={isDesktop ? { width: '16%' } : { width: '14%' }}>
                <Trans>MWR#</Trans>
              </ThStyled>
              <ThStyled style={{ width: '10%' }}>
                <Trans>MWR Status</Trans>
              </ThStyled>
              <ThStyled style={{ width: '8%' }}>
                <Trans>On Hand</Trans>
              </ThStyled>
            </tr>
          </thead>
          <tbody>
            {sortedMaterials.length > 0 ? (
              map(sortedMaterials, (item: Material, index) => {
                return (
                  <tr key={`${item.tagOrStockCode}-${index}`} data-testid="material-length">
                    <TdStyled data-testid="tagOrStockCode">
                      <Box>
                        <Text size="200" color="grey.600">
                          {item.tagOrStockCode}
                        </Text>
                      </Box>
                      <Box pt={{ sm: '200' }}>
                        <Text size="300" color="grey.1000">
                          {item.description}
                        </Text>
                      </Box>
                    </TdStyled>
                    <TdStyled>{item.iso}</TdStyled>
                    <TdStyled>
                      {item.design} / {item.issue} / {item.balance}
                    </TdStyled>
                    <TdStyled style={{ whiteSpace: 'pre-line' }}>{join(map(item.mwrs, 'mwrNumber'), '\n\n')}</TdStyled>
                    <TdStyled style={{ whiteSpace: 'pre-line' }}>{join(map(item.mwrs, 'mwrStatus'), '\n\n')}</TdStyled>
                    <TdStyled>{item.onHand}</TdStyled>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6}>
                  <Box p={{ sm: '450' }} justifyContent={{ sm: 'center' }} data-testid="no-material-found">
                    <Trans>No material information found.</Trans>
                  </Box>
                </td>
              </tr>
            )}
          </tbody>
        </TableStyled>
      )}
    </Box>
  );
};

interface MaterialsProps {
  onClose: () => void;
}

export const Materials: FC<MaterialsProps> = ({ onClose }) => {
  const { id: workPackageId, tag: workPackageTag } = useWorkPackage();

  return (
    <SideSheet size={SideSheetSize.LARGE} visible onDismiss={onClose}>
      <SideSheetHeader title={t`Material View`}>
        <SideSheetClose onClick={onClose}></SideSheetClose>
      </SideSheetHeader>
      <SideSheetSubHeader>
        <Box>
          <Heading color="grey.1000" weight="600">
            <Trans> WorkPackage</Trans>:
          </Heading>
          <Text size="500" color="grey.1000" data-testid="workpackge-tag">
            {workPackageTag}
          </Text>
        </Box>
      </SideSheetSubHeader>
      <SideSheetBody>
        <ErrorBoundary>
          <Suspense fallback={<Spinner />}>
            <MaterialsTable wpId={workPackageId} />
          </Suspense>
        </ErrorBoundary>
      </SideSheetBody>
    </SideSheet>
  );
};

export default Materials;
